import React, { useEffect, useMemo, useState } from "react";
import { storyblokEditable } from "@storyblok/react";
import { ProductDownloadsModel } from "./ProductDownloadsModel";
import DownloadsTable from "../DownloadsTable/DownloadsTable";
import { DownloadModel } from "../Download/DownloadModel";
import { Theme } from "../../core/Models/CustomSiteConfigModel";
import { themeService } from "../../core/NotificationStripNav/ThemeService";

import "./ProductDownloads.css";

const DOWNLOAD_OS_ORDER = ["Mac", "Windows", "iOS", "Android", "Browser"];

const getSearchTokens = (searchTerm: string) =>
  searchTerm.toLowerCase().trim().split(/\s+/).filter(Boolean);

const getDownloadSearchText = (
  productName: string,
  item: DownloadModel
) => {
  return [
    productName,
    item.productRelationship?.productName,
    item.downloadName,
    item.minimalOs,
    item.os,
    item.format,
    item.platform,
    ...(item.downloadTypes || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

const getOsSortIndex = (os: string) => {
  const index = DOWNLOAD_OS_ORDER.indexOf(os);
  return index === -1 ? DOWNLOAD_OS_ORDER.length : index;
};

const ProductDownloads: React.FC<{
  blok: ProductDownloadsModel;
  searchTerm: string;
}> = ({ blok, searchTerm }) => {
  const [theme, setTheme] = useState<Theme | null>(themeService.getTheme());
  const [isLegacy, setIsLegacy] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [activeDownloads, setActiveDownloads] = useState<DownloadModel[]>([]);
  const [legacyDownloads, setLegacyDownloads] = useState<DownloadModel[]>([]);

  const sortedDownloads = useMemo(
    () =>
      [...(blok.downloads || [])].sort((a, b) => {
        return getOsSortIndex(a.os) - getOsSortIndex(b.os);
      }),
    [blok.downloads]
  );

  useEffect(() => {
    const unsubscribe = themeService.subscribe(setTheme);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    const searchTokens = getSearchTokens(debouncedSearchTerm);
    const filtered =
      searchTokens.length === 0
        ? sortedDownloads
        : sortedDownloads.filter((item) => {
            const searchText = getDownloadSearchText(blok.productName, item);
            return searchTokens.every((token) => searchText.includes(token));
          });
    setActiveDownloads(filtered.filter((x) => x.versionType !== "legacy"));
    setLegacyDownloads(filtered.filter((x) => x.versionType === "legacy"));
    setIsLegacy(!!debouncedSearchTerm);
  }, [blok.productName, debouncedSearchTerm, sortedDownloads]);

  if (!blok.downloads) {
    return null;
  }

  if (
    (!activeDownloads || activeDownloads.length < 1) &&
    (!legacyDownloads || legacyDownloads.length < 1)
  ) {
    return null;
  }

  return (
    <div {...storyblokEditable(blok)} className={`product-downloads-comp`}>
      <div className="download-title">{blok.productName}</div>
      <DownloadsTable
        downloads={activeDownloads}
        key={blok._uid}
      ></DownloadsTable>
      <div className="show-legacy-container">
        <span
          className="underline-button"
          style={{
            color: theme?.textColor,
          }}
          onClick={() => setIsLegacy(!isLegacy)}
        >
          {isLegacy ? "HIDE LEGACY VERSIONS" : "SHOW LEGACY VERSIONS"}
        </span>
        {!isLegacy && (
          <span className="arrow" onClick={() => setIsLegacy(true)}>
            &#8595;
          </span>
        )}
        {isLegacy && (
          <span className="arrow" onClick={() => setIsLegacy(false)}>
            &#8593;
          </span>
        )}
      </div>

      {isLegacy && legacyDownloads.length > 0 && (
        <DownloadsTable
          downloads={legacyDownloads}
          key={`legacy-${blok._uid}`}
        ></DownloadsTable>
      )}
    </div>
  );
};

export default ProductDownloads;
